/**
 * Le **n° de parcelle** — `Site.cadastral_reference`
 * ([M30](../../../../docs/MODELE-DONNEES.md#m30)).
 *
 * La règle de format vit **une seule fois**, ici : le back-office et le terrain l'affichent tous
 * les deux, et le prototype l'imprime mot pour mot sous ses deux écrans —
 *
 * > Format parcelle : 2 lettres + 4 chiffres (ex. ET0090). 1 seule lettre → 0C 0090.
 *
 * ⚠️ **Elle assiste, elle ne bloque pas** (M13, M21) : la colonne reste un champ libre, et les
 * données reprises ne respectent pas toutes cette forme.
 */
/** 1 ou 2 lettres de section, puis jusqu'à 4 chiffres — les deux parties pouvant être collées. */
const CANONICAL = /^([A-Za-z]{1,2})\s*(\d{1,4})$/u;
/** Les six derniers caractères de la réf. GHP, d'où le repli tire la parcelle (M15, #121). */
const DERIVED_LENGTH = 6;
/**
 * Le n° de parcelle mis en forme, ou la valeur telle quelle quand elle n'a pas cette forme.
 *
 * ⚠️ **Rien n'est jamais refusé ni effacé.** Une référence héritée comme `BS 507 / 3` se rend
 * intacte : c'est la seule référence cadastrale du dossier, et la normaliser de force en
 * perdrait la moitié.
 */
export function formatCadastralReference(raw) {
    const trimmed = raw?.trim() ?? '';
    if (trimmed === '')
        return null;
    const match = CANONICAL.exec(trimmed);
    if (match === null)
        return trimmed;
    const [, letters, digits] = match;
    const number = digits.padStart(4, '0');
    // Le séparateur suit la section, pas une préférence : le prototype écrit `ET0090` collé et
    // `0C 0090` espacé, et ce sont les deux formes de son jeu validé.
    return letters.length === 2
        ? `${letters.toUpperCase()}${number}`
        : `0${letters.toUpperCase()} ${number}`;
}
/**
 * Le n° de parcelle **dérivé** de la réf. GHP — un repli d'affichage, jamais une écriture.
 *
 * La réf. GHP se compose d'un préfixe de commune et de la référence cadastrale (M15 :
 * `BOG` + `BS0507`), d'où la lecture des six derniers caractères.
 *
 * ⚠️ **Plus strict que `formatCadastralReference`, et pour une raison** : ce que la machine
 * devine ne se montre que s'il est sans ambiguïté. `Inquiry.reference` est un champ libre que
 * M1 interdit de parser, et les références héritées n'ont pas toutes cette forme — la queue de
 * `N20DI375-B29` n'est pas une parcelle. Mieux vaut ne rien afficher qu'un fragment de
 * référence présenté comme un cadastre.
 */
export function cadastralReferenceFromInquiryReference(reference) {
    const trimmed = reference?.trim() ?? '';
    if (trimmed.length < DERIVED_LENGTH)
        return null;
    const tail = trimmed.slice(-DERIVED_LENGTH);
    return CANONICAL.test(tail) ? formatCadastralReference(tail) : null;
}
/**
 * Le n° de parcelle **tel que les trois écrans l'affichent** : `d.parcelle || fmtParcelle(d.ref)`
 * — la valeur du dossier l'emporte, la dérivation n'est qu'un repli (M30).
 */
export function displayCadastralReference(dossier) {
    return (formatCadastralReference(dossier.cadastralReference) ??
        cadastralReferenceFromInquiryReference(dossier.reference));
}
//# sourceMappingURL=cadastral-reference.js.map